import * as React from "react"
import { type Editor, useEditorState } from "@tiptap/react"
import { HexColorPicker } from "react-colorful"

import { useMenuNavigation } from "../../../hooks/use-menu-navigation"
import { useIsMobile } from "../../../hooks/use-mobile"
import { useTiptapEditor } from "../../../hooks/use-tiptap-editor"

import { BanIcon } from "../../../components/tiptap-icons/ban-icon"

import type { ButtonProps } from "../../../components/tiptap-ui-primitive/button"
import { Button, ButtonGroup } from "../../../components/tiptap-ui-primitive/button"
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../components/tiptap-ui-primitive/popover"
import { Separator } from "../../../components/tiptap-ui-primitive/separator"
import {
  Card,
  CardBody,
  CardItemGroup,
} from "../../../components/tiptap-ui-primitive/card"

import type { UseColorFontConfig } from "../../../components/tiptap-ui/color-font-popover/use-color-font"
import {
  FONT_COLORS,
  useColorFont,
  type FontColor,
} from "../../../components/tiptap-ui/color-font-popover/use-color-font"

export interface ColorFontPopoverContentProps {
  editor?: Editor | null
  colors?: FontColor[]
}

export interface ColorFontPopoverProps
  extends Omit<ButtonProps, "type">,
    Pick<UseColorFontConfig, "editor" | "hideWhenUnavailable" | "onApplied"> {
  colors?: FontColor[]
}

export const ColorFontPopoverButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, children, ...props }, ref) => (
    <Button
      type="button"
      className={className}
      data-style="ghost"
      data-appearance="default"
      role="button"
      tabIndex={-1}
      aria-label="Cor da fonte"
      tooltip="Cor da fonte"
      ref={ref}
      {...props}
    >
      {children ?? <i className="fa-solid fa-font" />}
    </Button>
  )
)

ColorFontPopoverButton.displayName = "ColorFontPopoverButton"

export function ColorFontPopoverContent({
  editor,
  colors = FONT_COLORS,
}: ColorFontPopoverContentProps) {
  const { handleRemoveColor } = useColorFont({ editor })
  const isMobile = useIsMobile()
  const containerRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [showPicker, setShowPicker] = React.useState(false)
  const [customColor, setCustomColor] = React.useState("#000000")

  const menuItems = React.useMemo(
    () => [...colors, { label: "Remover cor", value: "none" }],
    [colors]
  )

  const { selectedIndex } = useMenuNavigation({
    containerRef,
    items: menuItems,
    orientation: "both",
    onSelect: (item: any) => {
      if (!containerRef.current) return false
      const highlighted = containerRef.current.querySelector(
        '[data-highlighted="true"]'
      ) as HTMLElement
      if (highlighted) highlighted.click()
      if (item.value === "none") handleRemoveColor()
    },
    autoSelectFirstItem: false,
  })

  const handleApplyCustomColor = React.useCallback(() => {
    if (!editor) return
    editor.chain().setMark("textStyle", { color: customColor }).run()
    editor.view.focus()
    setShowPicker(false)
  }, [editor, customColor])

  React.useEffect(() => {
    if (!showPicker) return

    const modal = document.querySelector('div.fade.ModalDefault.modal.show')

    if (modal) {
      modal.setAttribute('inert', '')
    }

    inputRef.current?.focus()

    return () => {
      if (modal) {
        modal.removeAttribute('inert')
      }
    }
  }, [showPicker])

  return (
    <Card
      ref={containerRef}
      tabIndex={isMobile ? -1 : 0}
      style={isMobile ? { boxShadow: "none", border: 0 } : {}}
    >
      <CardBody style={isMobile ? { padding: 0 } : {}}>
        <CardItemGroup orientation="horizontal">
          <ButtonGroup orientation="horizontal">
            {colors.map((color, index) => (
              <Button
                key={color.value}
                type="button"
                data-style="ghost"
                role="menuitem"
                tabIndex={index === selectedIndex ? 0 : -1}
                data-highlighted={selectedIndex === index}
                aria-label={`${color.label}`}
                tooltip={color.label}
                onClick={() => {
                  if (!editor) return
                  editor.chain().setMark("textStyle", { color: color.value }).run()
                }}
                style={{ padding: "4px" }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 18,
                    height: 18,
                    borderRadius: 3,
                    backgroundColor: color.value,
                    border: "1px solid rgba(0,0,0,0.15)",
                  }}
                />
              </Button>
            ))}
          </ButtonGroup>
          <Separator />
          <ButtonGroup orientation="horizontal">
            {!isMobile && (
              <Button
                type="button"
                data-style="ghost"
                role="menuitem"
                tabIndex={-1}
                aria-label="Mais cores"
                tooltip="Mais cores"
                onClick={() => setShowPicker((v) => !v)}
                style={{ fontSize: 11, padding: "2px 6px" }}
              >
                <i className="fa-solid fa-palette" />
              </Button>
            )}
            <Button
              onClick={handleRemoveColor}
              aria-label="Remover cor"
              tooltip="Remover cor"
              tabIndex={selectedIndex === colors.length ? 0 : -1}
              type="button"
              role="menuitem"
              data-style="ghost"
              data-highlighted={selectedIndex === colors.length}
            >
              <BanIcon className="tiptap-button-icon" />
            </Button>
          </ButtonGroup>
        </CardItemGroup>

        {showPicker && (
          <div style={{ padding: isMobile ? "12px 0" : "8px 4px 4px" }}>
            <div style={{ display: "flex", justifyContent: "center", padding: isMobile ? "0 12px" : "0" }}>
              <div style={{ transform: isMobile ? "scale(1.5)" : "scale(1)", transformOrigin: "top center" }}>
                <HexColorPicker color={customColor} onChange={setCustomColor} />
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: isMobile ? 16 : 8, padding: isMobile ? "0 12px" : "0" }}>
              <span
                style={{
                  display: "inline-block",
                  width: 24,
                  height: 24,
                  borderRadius: 4,
                  backgroundColor: customColor,
                  border: "1px solid rgba(0,0,0,0.2)",
                  flexShrink: 0,
                }}
              />
              <input
                ref={inputRef}
                type="text"
                value={customColor.toUpperCase()}
                onChange={(e) => {
                  const val = e.target.value
                  if (/^#[0-9A-Fa-f]{6}$/.test(val) || val.length <= 7) {
                    setCustomColor(val.startsWith("#") ? val : `#${val}`)
                  }
                }}
                  onPaste={(e) => {
                const pastedText = e.clipboardData.getData("text")
                const hex = pastedText.startsWith("#") ? pastedText : `#${pastedText}`
                setCustomColor(hex.toUpperCase())
              }}
                placeholder="#000000"
                style={{
                  flex: 1,
                  fontSize: 12,
                  fontFamily: "monospace",
                  color: "var(--primary-20)",
                  padding: "4px 6px",
                  border: "1px solid rgba(0,0,0,0.1)",
                  borderRadius: 3,
                  outline: "none",
                  backgroundColor: "transparent",
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleApplyCustomColor()
                  }
                }}
              />
              <Button
                type="button"
                data-style="ghost"
                onClick={handleApplyCustomColor}
                style={{ fontSize: 12, padding: "2px 10px" }}
              >
                Aplicar
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

export function ColorFontPopover({
  editor: providedEditor,
  colors = FONT_COLORS,
  hideWhenUnavailable = false,
  onApplied,
  ...props
}: ColorFontPopoverProps) {
  const { editor } = useTiptapEditor(providedEditor)
  const { isVisible, canSetColor, isActive } = useColorFont({
    editor,
    hideWhenUnavailable,
    onApplied,
  })

  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => {
      if (!editor) return { currentColor: null }

      const attrs = editor.getAttributes("textStyle")
      const storedMarks = editor.state.storedMarks

      const storedColor = storedMarks
        ?.filter((mark) => mark.type.name === "textStyle")
        ?.map((mark) => mark.attrs?.color)
        ?.find(Boolean)

      return {
        currentColor: attrs.color || storedColor || null,
      }
    },
  })

  const currentColor = editorState?.currentColor ?? null
  if (!isVisible) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <ColorFontPopoverButton
          disabled={!canSetColor}
          data-disabled={!canSetColor}
          aria-label="Cor da fonte"
          tooltip="Cor da fonte"
          data-active-state={isActive ? "on" : "off"}
          aria-pressed={isActive}
          {...props}
        >
          <span style={{ position: "relative", display: "inline-flex" }}>
            <i className="fa-solid fa-font" />
            <span
              style={{
                position: "absolute",
                bottom: -3,
                left: 0,
                right: 0,
                height: 3,
                backgroundColor: currentColor || "transparent",
                borderRadius: 2,
              }}
            />
          </span>
        </ColorFontPopoverButton>
      </PopoverTrigger>

      <PopoverContent aria-label="Cores da fonte" onFocusOutside={(e) => e.preventDefault()}>
        <ColorFontPopoverContent editor={editor} colors={colors} />
      </PopoverContent>
    </Popover>
  )
}

export default ColorFontPopover
